import type { IReferenceGroupFavorites } from '@repo/reference-wells';

import type { IUserPreferenceService } from 'src/packages/user-preference/IUserPreferenceService';

function isFavoriteMap(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

/** Only a non-empty string id counts. Anything else is “no favorite”. */
function readFavoriteGroupId(map: unknown, wellId: number): string | null {
  if (!isFavoriteMap(map)) {
    return null;
  }

  const value = map[String(wellId)];

  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();

  return trimmed === '' ? null : trimmed;
}

function nextFavoriteMap(current: unknown, wellId: number, groupId: string | null): Record<string, string | null> {
  const next: Record<string, string | null> = {};

  if (isFavoriteMap(current)) {
    for (const [key, value] of Object.entries(current)) {
      if (value === null) {
        next[key] = null;
        continue;
      }

      if (typeof value !== 'string') {
        continue;
      }

      const trimmed = value.trim();

      if (trimmed !== '') {
        next[key] = trimmed;
      }
    }
  }

  next[String(wellId)] = groupId;

  return next;
}

/**
 * Adapts dashboard UserPreference into the reference-wells favorites port.
 * One favorite group id per well. Invalid stored values are ignored.
 */
export class ReferenceGroupFavoritesAdapter implements IReferenceGroupFavorites {
  constructor(private readonly userPreference: IUserPreferenceService) {}

  getFavoriteGroupId(wellId: number): string | null {
    return readFavoriteGroupId(this.userPreference.preferences.favoriteReferenceGroupIdByWell, wellId);
  }

  setFavorite(wellId: number, groupId: string | null): void {
    this.userPreference.updatePreferences({
      favoriteReferenceGroupIdByWell: nextFavoriteMap(
        this.userPreference.preferences.favoriteReferenceGroupIdByWell,
        wellId,
        groupId,
      ),
    });
  }
}
